// Decides where a modal dialog belongs while the on-screen keyboard has panned
// the visual viewport. Pure: the DOM side lives in
// app-common/services/modal-keyboard-avoidance.ts.

/** window.visualViewport's geometry, in CSS px. offsetTop/offsetLeft are the
 * visual viewport's position within the layout viewport — the same coordinate
 * space getBoundingClientRect reports fixed elements in. */
export interface ViewportBandSnapshot {
    offsetTop: number;
    offsetLeft: number;
    height: number;
    width: number;
}

export interface DialogNaturalMetrics {
    /** Client top of the dialog with no fit transform applied. */
    top: number;
    /** Height with no fit max-height applied. */
    height: number;
}

export interface DialogFit {
    /** Vertical shift that puts the dialog inside the band; 0 leaves it alone. */
    translateY: number;
}

export const BAND_MARGIN_PX = 8;

export function fitDialogToViewportBand(
    viewport: ViewportBandSnapshot,
    dialog: DialogNaturalMetrics,
    margin: number = BAND_MARGIN_PX,
    /** The focused field's natural metrics, same coordinate space as the
     * dialog. When given, keeping IT in the band outranks the dialog's edges:
     * the caret the user is typing at must never end under the keyboard. */
    field?: DialogNaturalMetrics,
): DialogFit {
    // A dialog taller than the band is NEVER capped or internally scrolled
    // (Crane, 23 Sep, on device): it keeps its top pinned to the band and its
    // tail runs under the keyboard, the field rules below slide whichever
    // field is focused into view, and closing the keyboard restores the
    // whole dialog. These edit dialogs have never scrolled and must not start.
    let translateY = 0;
    const bandTop = viewport.offsetTop + margin;
    const bandBottom = viewport.offsetTop + viewport.height - margin;

    if (dialog.top + dialog.height > bandBottom) {
        translateY = bandBottom - (dialog.top + dialog.height);
    }
    if (dialog.top + translateY < bandTop) {
        // Second so it wins: a dialog that cannot satisfy both edges keeps its top visible.
        translateY = bandTop - dialog.top;
    }

    if (field) {
        if (field.top + field.height + translateY > bandBottom) {
            translateY = bandBottom - (field.top + field.height);
        }
        if (field.top + translateY < bandTop) {
            // Second so it wins, same shape as the dialog rule above.
            translateY = bandTop - field.top;
        }
    }
    return { translateY };
}
